import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Play, ArrowLeft, CheckCircle, XCircle, Clock, Maximize, Minimize, Eye, Copy, Terminal, Sliders, X } from 'lucide-react';
import { Exercise, UserSettings, Language, LearningMode, ProgrammingLanguage, ExerciseType, GENERAL_CORRECTION_THRESHOLD, QUEST_CORRECTION_THRESHOLD, type KnowledgePointProgressView } from '@unity-learning/shared';
import { errorMessage, generateExercise, getAttemptReview, getExercise, getKnowledgePointProgress, getUserSettings, isApiError, submitExercise } from '../services/api';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';
import ReactMarkdown from 'react-markdown';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { cpp } from '@codemirror/lang-cpp';
import { oneDark } from '@codemirror/theme-one-dark';
import { githubLight } from '@uiw/codemirror-theme-github';
import { EditorView, Decoration, DecorationSet, ViewPlugin, ViewUpdate } from '@codemirror/view';
import { StateField, StateEffect, RangeSetBuilder, Prec, countColumn } from '@codemirror/state';
import { PedagogicalChatbot } from '../components/PedagogicalChatbot';
import AdminQuestButton from '../components/AdminQuestButton';
import DifficultySettingsModal from '../components/DifficultySettingsModal';
import ExerciseMiniCours from '../components/ExerciseMiniCours';
import { GenerationFailedNotice } from '../components/GenerationFailedNotice';
import { FollowUpButtons, KnowledgeAcquiredNotice, KnowledgeProgressLine } from '../components/FollowUpButtons';
import { SimulatorExercise } from '../components/simulator/SimulatorExercise';
import { trackEvent } from '../utils/analytics';
import { getLanguageExtension, STUDENT_EDITOR_BASIC_SETUP } from '../utils/studentEditor';
import { extractStarterTodos } from '../utils/starterTodos';
import { splitInstructionParts } from '../utils/instructionParts';
import { buildPathCustomPrompt, findPathNode, knowledgeTarget, type FollowUpAction } from '../utils/followUpActions';
import {
  recordExerciseSuccess,
  recordExerciseFailure,
  markDifficultyCtaClicked,
  markDifficultyCtaDismissed,
  markDifficultyAdjusted
} from '../utils/difficultyCta';

// Custom C# friendly dark theme
// Si l'erreur n'a pas de message (cas imprévu) ; d'ordinaire, c'est le message du serveur qui s'affiche.
// Où le bouton a été cliqué : le bandeau d'échec s'affiche juste en dessous (en relecture, les deux barres coexistent).
type FollowUpOrigin = 'editor' | 'feedback';

const GENERATION_FAILED_FALLBACK = "L'exercice n'a pas pu être créé. Réessayez, un nouvel essai suffit en général.";

const csharpDarkTheme = EditorView.theme({
  '&': {
    color: '#d4d4d4',
    backgroundColor: '#1e1e1e'
  },
  '.cm-content': {
    padding: '16px',
    fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", monospace',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  '.cm-focused': {
    outline: 'none'
  },
  '.cm-editor': {
    borderRadius: '8px'
  },
  '.cm-scroller': {
    fontFamily: 'inherit'
  },
  '.cm-gutters': {
    backgroundColor: '#252526',
    color: '#858585',
    border: 'none',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px'
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#2a2d2e'
  },
  '.cm-activeLine': {
    backgroundColor: '#2a2d2e'
  },
  '.cm-selectionBackground': {
    backgroundColor: '#264f78'
  },
  '.cm-cursor': {
    borderLeftColor: '#ffffff'
  },
  '.cm-searchMatch': {
    backgroundColor: '#515c6a'
  },
  '.cm-searchMatch.cm-searchMatch-selected': {
    backgroundColor: '#6f8cc4'
  }
}, { dark: true });

// Consignes : du Markdown, et un éditeur en lecture seule pour chaque bloc ``` (splitInstructionParts)
const InstructionRenderer: React.FC<{ text: string }> = ({ text }) => {
  const parts = splitInstructionParts(text);

  return (
    <div className="text-gray-800 dark:text-gray-200 leading-relaxed">
      {parts.map((part, index) => {
        if (part.kind === 'code') {
          if (!part.code.trim()) return null;
          return (
            <div key={index} className="my-4 rounded-lg overflow-hidden">
              <CodeMirror
                value={part.code}
                theme={[oneDark, csharpDarkTheme]}
                extensions={[cpp()]}
                height="auto"
                editable={false}
                basicSetup={{
                  lineNumbers: true,
                  foldGutter: false,
                  dropCursor: false,
                  allowMultipleSelections: false,
                  indentOnInput: false,
                  bracketMatching: true,
                  closeBrackets: false,
                  autocompletion: false,
                  highlightSelectionMatches: false,
                  searchKeymap: false,
                  tabSize: 4,
                }}
              />
            </div>
          );
        }

        // Regular text with ReactMarkdown for formatting. Le gras garde la couleur du texte :
        // une couleur de la famille du code fait croire à l'élève que le mot est à écrire.
        return (
          <ReactMarkdown
            key={index}
            components={{
              strong: ({ children }) => <strong className="font-bold text-inherit">{children}</strong>,
              code: ({ children }) => (
                <code className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-1.5 py-0.5 rounded text-sm font-mono">
                  {children}
                </code>
              ),
              p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
              ul: ({ children }) => <ul className="list-disc pl-5 mb-3">{children}</ul>,
              // start : une liste coupée par un bloc de code reprend à son numéro, pas à 1
              ol: ({ children, start }) => <ol start={start} className="list-decimal pl-5 mb-3">{children}</ol>,
              li: ({ children }) => <li className="mb-1">{children}</li>,
            }}
          >
            {part.text}
          </ReactMarkdown>
        );
      })}
    </div>
  );
};

// --- Console d'erreurs (analyse IA à la soumission) ---

interface CompilationIssue {
  severity: 'error' | 'warning';
  line: number | null;
  message: string;
}

// Best-effort : extrait un préfixe « Ligne N: » / « Line N: » du message d'évaluation.
// Pas de ligne parsable → pas de surlignage, jamais d'erreur.
const parseIssueLine = (message: string): number | null => {
  const match = message.match(/^\s*(?:ligne|line)\s+(\d{1,5})\s*[:.)\-]?/i);
  if (!match) return null;
  const line = parseInt(match[1], 10);
  return Number.isFinite(line) && line > 0 ? line : null;
};

const setIssueLines = StateEffect.define<{ line: number; severity: 'error' | 'warning' }[]>();

const errorLineDecoration = Decoration.line({ class: 'cm-issueLine-error' });
const warningLineDecoration = Decoration.line({ class: 'cm-issueLine-warning' });

// Surlignage des lignes signalées par l'évaluation ; retiré à la première édition
// du code (pas de mapping des positions : le surlignage n'est plus fiable après édition)
const issueLinesField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(decorations, tr) {
    if (tr.docChanged) return Decoration.none;
    for (const effect of tr.effects) {
      if (effect.is(setIssueLines)) {
        const bySeverity = new Map<number, 'error' | 'warning'>();
        for (const { line, severity } of effect.value) {
          if (line < 1 || line > tr.state.doc.lines) continue; // lignes hors document ignorées
          if (severity === 'error' || !bySeverity.has(line)) bySeverity.set(line, severity);
        }
        const builder = new RangeSetBuilder<Decoration>();
        [...bySeverity.entries()]
          .sort(([lineA], [lineB]) => lineA - lineB)
          .forEach(([line, severity]) => {
            const { from } = tr.state.doc.line(line);
            builder.add(from, from, severity === 'error' ? errorLineDecoration : warningLineDecoration);
          });
        return builder.finish();
      }
    }
    return decorations;
  },
  provide: (field) => EditorView.decorations.from(field),
});

const issueLinesTheme = EditorView.baseTheme({
  '.cm-issueLine-error': {
    backgroundColor: 'rgba(239, 68, 68, 0.14)',
    boxShadow: 'inset 3px 0 0 rgba(239, 68, 68, 0.6)'
  },
  '.cm-issueLine-warning': {
    backgroundColor: 'rgba(245, 158, 11, 0.10)',
    boxShadow: 'inset 3px 0 0 rgba(245, 158, 11, 0.5)'
  }
});

// Les lignes trop longues (le plus souvent le commentaire TODO) passent à la ligne au lieu
// d'obliger à défiler horizontalement. La suite d'une ligne coupée est décalée de
// l'indentation de cette ligne : sans ce décalage, elle repartirait contre la marge et se
// lirait comme une nouvelle instruction.
const wrappedLineIndent = ViewPlugin.fromClass(class {
  decorations: DecorationSet;

  constructor(view: EditorView) {
    this.decorations = this.build(view);
  }

  update(update: ViewUpdate) {
    if (update.docChanged || update.viewportChanged) this.decorations = this.build(update.view);
  }

  build(view: EditorView): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>();
    for (const { from, to } of view.visibleRanges) {
      for (let pos = from; pos <= to;) {
        const line = view.state.doc.lineAt(pos);
        const indent = countColumn(/^[ \t]*/.exec(line.text)![0], view.state.tabSize);
        if (indent > 0) {
          // 6px : la marge gauche que CodeMirror donne à chaque ligne
          builder.add(line.from, line.from, Decoration.line({
            attributes: { style: `padding-left: calc(${indent}ch + 6px); text-indent: -${indent}ch` }
          }));
        }
        pos = line.to + 1;
      }
    }
    return builder.finish();
  }
}, { decorations: (plugin) => plugin.decorations });

// Hors plein écran, l'éditeur prend la hauteur du script (une hauteur fixe laissait un grand
// vide sous les scripts courts et repoussait la correction sous l'écran). Marge du bas : 4.5em,
// soit trois lignes de 1.5em (line-height de csharpDarkTheme). Le minimum est posé sur
// .cm-content et .cm-gutter, comme le recommande CodeMirror, et non sur l'éditeur lui-même.
// Prec.highest : entre deux thèmes, CodeMirror donne raison au premier de la liste des
// extensions, et le `padding: 16px` de csharpDarkTheme écraserait la marge du bas.
const scriptHeightTheme = Prec.highest(EditorView.theme({
  '.cm-content': { paddingBottom: 'calc(16px + 4.5em)' },
  '.cm-content, .cm-gutter': { minHeight: '200px' }
}));

// Enhanced Code Editor Component with CodeMirror syntax highlighting
const CodeEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
  language: string;
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
  issueLines?: { line: number; severity: 'error' | 'warning' }[];
}> = ({ value, onChange, language, isFullScreen, onToggleFullScreen, issueLines }) => {

  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const issueLinesRef = useRef(issueLines);
  issueLinesRef.current = issueLines;

  // Applique / retire les surlignages quand l'évaluation change
  useEffect(() => {
    const view = editorRef.current?.view;
    if (view) view.dispatch({ effects: setIssueLines.of(issueLines ?? []) });
  }, [issueLines]);

  // Pédagogie : le collage est désactivé, les étudiants tapent tout à la main
  // (même règle que l'autocomplétion coupée)
  const [pasteBlocked, setPasteBlocked] = useState(false);
  const pasteTimerRef = useRef<number | null>(null);

  const notifyPasteBlocked = useCallback(() => {
    setPasteBlocked(true);
    if (pasteTimerRef.current) window.clearTimeout(pasteTimerRef.current);
    pasteTimerRef.current = window.setTimeout(() => setPasteBlocked(false), 3500);
  }, []);

  useEffect(() => () => {
    if (pasteTimerRef.current) window.clearTimeout(pasteTimerRef.current);
  }, []);

  const blockPasteExtension = useMemo(() => EditorView.domEventHandlers({
    paste: (event) => {
      event.preventDefault();
      notifyPasteBlocked();
      return true;
    },
    drop: (event) => {
      event.preventDefault();
      notifyPasteBlocked();
      return true;
    },
  }), [notifyPasteBlocked]);

  const containerClasses = isFullScreen
    ? "fixed inset-0 z-50 bg-gray-900 flex flex-col"
    : "relative";

  // Hors plein écran : hauteur du script (voir scriptHeightTheme), plafonnée à l'ancienne
  // hauteur fixe ; au-delà, l'éditeur défile à l'intérieur
  const editorHeight = isFullScreen ? "100%" : "auto";

  return (
    <div className={`${containerClasses} code-editor`}>
      {isFullScreen && (
        <div className="flex items-center justify-between p-4 border-b bg-gray-800 text-white">
          <h3 className="text-lg font-semibold">Éditeur de code - {language}</h3>
          <button
            onClick={onToggleFullScreen}
            className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded"
          >
            <Minimize className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* min-h-0 : borne la zone d'édition à l'espace disponible en plein écran,
          pour que le scroll interne de CodeMirror fonctionne au lieu de couper le contenu */}
      <div className={isFullScreen ? "flex-1 min-h-0 relative" : "relative rounded-lg overflow-hidden"}>
        <CodeMirror
          ref={editorRef}
          value={value}
          onChange={(val) => onChange(val)}
          onCreateEditor={(view) => {
            // Mode révision : le feedback peut être présent avant la création de l'éditeur
            if (issueLinesRef.current?.length) {
              view.dispatch({ effects: setIssueLines.of(issueLinesRef.current) });
            }
          }}
          className={isFullScreen ? "h-full" : undefined}
          theme={[oneDark, csharpDarkTheme]}
          extensions={[
            ...getLanguageExtension(language),
            blockPasteExtension,
            issueLinesField,
            issueLinesTheme,
            EditorView.lineWrapping,
            wrappedLineIndent,
            ...(isFullScreen ? [] : [scriptHeightTheme])
          ]}
          height={editorHeight}
          maxHeight={isFullScreen ? undefined : "calc(100vh - 300px)"}
          placeholder="Écrivez votre code ici..."
          basicSetup={{ ...STUDENT_EDITOR_BASIC_SETUP }}
        />

        {!isFullScreen && (
          <div className="absolute top-2 right-2 flex items-center space-x-2">
            <button
              onClick={onToggleFullScreen}
              className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
              title="Plein écran"
            >
              <Maximize className="w-4 h-4" />
            </button>
            <div className="bg-gray-700 px-2 py-1 rounded text-xs text-gray-300">
              {language}
            </div>
          </div>
        )}

        {isFullScreen && (
          <div className="absolute bottom-4 right-4">
            <div className="bg-gray-700 px-3 py-1 rounded text-sm text-gray-300">
              {language} - Échap pour quitter le plein écran
            </div>
          </div>
        )}

        {pasteBlocked && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-amber-500 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-lg pointer-events-none">
            ✍️ Le collage est désactivé — écrire le code soi-même, c'est comme ça qu'on le retient !
          </div>
        )}
      </div>
    </div>
  );
};

// Correction Section Component
const CorrectionSection: React.FC<{
  correctSolution: string;
  /** Appelé quand la solution est affichée : le Coach le sait (et sait que cela ne coûte aucun point). */
  onReveal: (correctSolution: string) => void;
}> = ({ correctSolution, onReveal }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isVisible) onReveal(correctSolution);
  }, [isVisible, correctSolution, onReveal]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(correctSolution);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-gray-900 dark:text-white">Solution correcte</h4>
        <button
          onClick={() => setIsVisible(!isVisible)}
          className="inline-flex items-center px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
        >
          <Eye className="w-4 h-4 mr-1" />
          {isVisible ? 'Masquer' : 'Voir la correction'}
        </button>
      </div>

      {isVisible && (
        <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Utilisez cette solution pour comprendre l'approche correcte
            </span>
            <button
              onClick={handleCopy}
              className="inline-flex items-center px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
            >
              <Copy className="w-3 h-3 mr-1" />
              {copied ? 'Copié!' : 'Copier'}
            </button>
          </div>

          <CodeMirror
            value={correctSolution}
            theme={[oneDark, csharpDarkTheme]}
            extensions={[cpp()]}
            height="auto"
            editable={false}
            basicSetup={{
              lineNumbers: true,
              foldGutter: false,
              dropCursor: false,
              allowMultipleSelections: false,
              indentOnInput: false,
              bracketMatching: true,
              closeBrackets: false,
              autocompletion: false,
              highlightSelectionMatches: false,
              searchKeymap: false,
              tabSize: 4,
            }}
          />
        </div>
      )}
    </div>
  );
};

interface ExercisePageProps {
  mode?: 'new' | 'review';
}

const ExercisePage: React.FC<ExercisePageProps> = ({ mode = 'new' }) => {
  const { exerciseId, attemptId } = useParams<{ exerciseId: string; attemptId?: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const { learningMode, programmingLanguage } = useLearningMode();

  // Get URL search parameters for learningNodeId and knowledgePointIds
  const urlParams = new URLSearchParams(window.location.search);
  const urlLearningNodeId = urlParams.get('learningNodeId');
  const urlKnowledgePointIds = urlParams.get('knowledgePointIds')?.split(',').filter(Boolean) || [];

  const [exercise, setExercise] = useState<Exercise | null>(null);

  // Le parcours d'où vient l'exercice : l'adresse d'abord, sinon ce que l'exercice a gardé de sa
  // génération (révision d'une ancienne tentative, exercice rouvert depuis l'historique).
  const learningNodeId = urlLearningNodeId ?? exercise?.learningNodeId ?? null;
  const knowledgePointIds = urlKnowledgePointIds.length > 0 ? urlKnowledgePointIds : (exercise?.knowledgePointIds ?? []);
  const [code, setCode] = useState('');

  // Code de départ par défaut selon le mode d'apprentissage
  const getDefaultStartingCode = () => {
    if (learningMode === LearningMode.PYTHON_JS) {
      return programmingLanguage === ProgrammingLanguage.PYTHON
        ? '# Écrivez votre code ici\n'
        : '// Écrivez votre code ici\n';
    }
    if (learningMode === LearningMode.GODOT) {
      return 'extends Node\n\nfunc _ready():\n\t# Écrivez votre code ici\n\tpass\n';
    }
    return `using UnityEngine;

public class GameScript : MonoBehaviour
{
    void Start()
    {
        // Complétez le code ici
    }

    void Update()
    {
        // Complétez le code ici
    }
}`;
  };

  // Adresse d'un exercice avec son contexte de parcours (étape et points visés), porté jusqu'à la soumission
  const buildExerciseUrl = (newExerciseId: string, nodeId: string | null, kpIds: string[]): string => {
    const params = new URLSearchParams();
    if (nodeId) {
      params.set('learningNodeId', nodeId);
    }
    if (kpIds.length > 0) {
      params.set('knowledgePointIds', kpIds.join(','));
    }
    return params.toString()
      ? `/exercise/${newExerciseId}?${params.toString()}`
      : `/exercise/${newExerciseId}`;
  };
  const generateExerciseUrl = (newExerciseId: string): string => buildExerciseUrl(newExerciseId, learningNodeId, knowledgePointIds);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Échec d'un des trois boutons d'après-exercice : affiché sous les boutons, sans quitter l'exercice.
  const [followUpFailure, setFollowUpFailure] = useState<{ kind: FollowUpAction; origin: FollowUpOrigin; message: string } | null>(null);
  useEffect(() => { setFollowUpFailure(null); }, [exerciseId]);
  // Génération en cours depuis l'un de ces trois boutons. La page reste affichée pendant l'attente
  // (pas d'écran « Chargement de l'exercice... ») : le navigateur remontait en haut de page, et le
  // bandeau d'échec, rendu sous le bouton cliqué, se retrouvait hors de l'écran.
  const [followUpPending, setFollowUpPending] = useState<FollowUpAction | null>(null);
  const followUpFailureRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (followUpFailure) followUpFailureRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [followUpFailure]);
  const [isCodeFullScreen, setIsCodeFullScreen] = useState(false);
  const [feedback, setFeedback] = useState<{
    success: boolean;
    message: string;
    suggestions?: string[];
    detailedFeedback?: {
      score: number;
      compilationStatus: {
        wouldCompile: boolean;
        errors: string[];
        warnings: string[];
        scoreCapped?: boolean; // A4 : plafond 60 appliqué (absent sur les anciens feedbacks)
        notAnalyzed?: boolean; // copie inchangée : le serveur n'a rien analysé
      };
      conceptMastery: Record<string, number>;
      correctSolution?: string;
      // New pedagogical fields
      whatWorked?: string[];
      keyIssues?: string[];
      learningInsights?: string[];
      nextSteps?: string[];
    };
  } | null>(null);
  // La solution que l'élève a vraiment affichée (« Voir la correction ») : seule celle de la copie en cours compte pour le Coach.
  const [revealedSolution, setRevealedSolution] = useState<string | null>(null);

  // Review mode specific state
  const [reviewAttempt, setReviewAttempt] = useState<any>(null);
  const [originalCode, setOriginalCode] = useState('');

  // Chatbot state
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);

  // B6 — CTA « Trop facile ? » après une série de réussites (compteur dans utils/difficultyCta)
  const [showDifficultyCta, setShowDifficultyCta] = useState(false);
  const [ctaStreak, setCtaStreak] = useState(0);
  const [isDifficultyModalOpen, setIsDifficultyModalOpen] = useState(false);

  // Time tracking
  const [startTime, setStartTime] = useState(new Date());

  // User settings for correction button
  const [userSettings, setUserSettings] = useState<UserSettings>({
    showCorrectionButton: true,
    preferredLanguage: Language.FRENCH
  });

  // Quest detection
  // Une quête, active ou terminée, ne montre sa correction qu'à partir de 90 (le serveur le dit avec l'exercice)
  const isQuestExercise = Boolean(exercise?.isQuest);

  // Où en est l'élève sur le point de connaissance de l'exercice (exercices du parcours seulement) :
  // repère en haut de page, compteur, annonce d'acquisition, cible du bouton « Connaissance suivante ».
  const [kpProgress, setKpProgress] = useState<KnowledgePointProgressView | null>(null);
  const [acquiredNow, setAcquiredNow] = useState<KnowledgePointProgressView | null>(null);
  const progressKpId = learningNodeId && knowledgePointIds.length > 0 ? knowledgePointIds[0] : null;
  const progressKey = exercise && progressKpId ? `${exercise.id}:${progressKpId}` : null;
  const progressKeyRef = useRef<string | null>(null);
  progressKeyRef.current = progressKey;
  useEffect(() => {
    setKpProgress(null);
    setAcquiredNow(null);
    if (!user?.id || !progressKpId || !progressKey) return;
    let cancelled = false;
    getKnowledgePointProgress(user.id, progressKpId)
      .then(view => { if (!cancelled) setKpProgress(view); })
      .catch(() => { /* sans repère, l'exercice garde ses deux boutons */ });
    return () => { cancelled = true; };
  }, [user?.id, progressKpId, progressKey]);

  // Auto-scroll to feedback when it appears
  useEffect(() => {
    if (feedback) {
      const feedbackElement = document.getElementById('feedback-section');
      if (feedbackElement) {
        feedbackElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }
  }, [feedback]);

  // Handle escape key for full-screen mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isCodeFullScreen) {
        setIsCodeFullScreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isCodeFullScreen]);

  // Fetch user settings
  useEffect(() => {
    const fetchUserSettings = async () => {
      if (!user?.id) return;

      try {
        const settings = await getUserSettings(user.id);
        if (settings) {
          // Ensure preferredLanguage has a fallback for backward compatibility
          setUserSettings({
            ...settings,
            preferredLanguage: settings.preferredLanguage || Language.FRENCH
          });
        }
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres utilisateur:', error);
      }
    };

    fetchUserSettings();
  }, [user?.id]);

  useEffect(() => {
    const fetchData = async () => {
      if (!exerciseId) {
        console.error('❌ Aucun ID d\'exercice fourni');
        setError('ID d\'exercice manquant');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      setFeedback(null); // Clear previous feedback when loading new exercise

      try {
        if (mode === 'review' && attemptId) {
          // Review mode: fetch attempt with feedback
          console.log('🔍 Mode révision - Récupération de la tentative:', attemptId);
          let review;
          try {
            review = await getAttemptReview(exerciseId, attemptId);
          } catch (loadError) {
            if (isApiError(loadError) && loadError.status === 404) {
              throw new Error('Cette tentative n\'existe pas ou a été supprimée.');
            }
            throw loadError;
          }
          console.log('📦 Données de révision reçues:', review);

          if (review) {
            const { exercise: exerciseData, attempt, feedback: feedbackData } = review;
            setExercise(exerciseData);
            setReviewAttempt(attempt);
            setCode(attempt.solution); // Show user's submitted code
            setOriginalCode(exerciseData.expectedSolution || ''); // Store original template

            // Set feedback if available - support both old and new feedback formats
            if (feedbackData) {
              setFeedback({
                success: attempt.isCorrect,
                message: feedbackData.logicAnalysis,
                suggestions: [
                  // Legacy fallback for old feedback format
                  ...(feedbackData.bestPractices || []),
                  ...(feedbackData.improvements || []),
                  ...(feedbackData.compilationStatus.errors.length > 0
                    ? [`Erreurs de compilation: ${feedbackData.compilationStatus.errors.join(', ')}`]
                    : []),
                  ...(feedbackData.compilationStatus.warnings.length > 0
                    ? [`Avertissements: ${feedbackData.compilationStatus.warnings.join(', ')}`]
                    : [])
                ],
                detailedFeedback: {
                  score: feedbackData.overallScore,
                  compilationStatus: feedbackData.compilationStatus,
                  conceptMastery: feedbackData.conceptMastery,
                  correctSolution: feedbackData.correctSolution,
                  // New pedagogical fields - with fallbacks for old data
                  whatWorked: feedbackData.whatWorked || feedbackData.bestPractices || [],
                  keyIssues: feedbackData.keyIssues || [],
                  learningInsights: feedbackData.learningInsights || [],
                  nextSteps: feedbackData.nextSteps || feedbackData.improvements || []
                }
              });
            }

            console.log('✅ Tentative chargée avec succès pour révision');
          } else {
            throw new Error('Erreur lors de la récupération de la tentative');
          }
        } else {
          // Normal mode: fetch exercise only
          console.log('🔍 Mode normal - Récupération de l\'exercice avec ID:', exerciseId);
          let loaded: Exercise;
          try {
            loaded = await getExercise(exerciseId);
          } catch (loadError) {
            if (isApiError(loadError) && loadError.status === 404) {
              throw new Error('Cet exercice n\'existe pas ou a été supprimé.');
            }
            throw loadError;
          }
          console.log('📦 Données d\'exercice reçues:', loaded);

          if (loaded) {
            console.log('✅ Exercice chargé avec succès:', loaded.title);
            setExercise(loaded);

            // Use the starting code from the exercise, or fallback to mode-aware template
            const initialCode = loaded.startingCode || getDefaultStartingCode();

            setCode(initialCode);

            // Reset start time for new exercise
            setStartTime(new Date());
          } else {
            throw new Error('Impossible de charger l\'exercice');
          }
        }
      } catch (err) {
        console.error('❌ Erreur lors de la récupération:', err);
        setError((err as Error).message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [exerciseId, attemptId, mode]);

  const handleSubmit = async () => {
    if (!exercise || !exerciseId || !user) return;

    setIsSubmitting(true);
    setFeedback(null); // Nouvelle tentative : nettoie le feedback, la console et les surlignages
    setAcquiredNow(null);
    setShowDifficultyCta(false); // B6 : le CTA suit le feedback courant
    const progressBefore = kpProgress;
    const progressKeyAtSubmit = progressKey;

    try {
      // Calculate actual time spent in seconds
      const timeSpent = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);

      // Get the original starting code for context (mode-aware defaults)
      const originalCode = exercise.startingCode || getDefaultStartingCode();

      // Call the backend API for AI-powered evaluation
      const result = await submitExercise(exerciseId, {
        exerciseId,
        userId: user.id,
        solution: code,
        originalCode, // Include the starting template
        timeSpent, // Actual time spent in seconds
        learningNodeId, // Learning path context if available
        ...(knowledgePointIds.length > 0 && { knowledgePointIds }), // Knowledge points if available
        learningMode,
        programmingLanguage
      });

      if (result && result.feedback) {
        const feedback = result.feedback;

        // Filter out suggestions that might contain the correct solution code
        const filterCodeSuggestions = (suggestions: string[]) => {
          if (!feedback.correctSolution) return suggestions;

          return suggestions.filter(suggestion => {
            // Remove suggestions that look like they contain code solutions
            const containsCodeBlock = suggestion.includes('```') ||
              suggestion.includes('using UnityEngine') ||
              suggestion.includes('public class') ||
              suggestion.includes('void ') ||
              suggestion.includes('extends Node') ||
              suggestion.includes('func _') ||
              (suggestion.includes('{') && suggestion.includes('}'));

            // Also remove if it explicitly mentions "solution" or "correct code"
            const mentionsSolution = suggestion.toLowerCase().includes('solution correcte') ||
              suggestion.toLowerCase().includes('correct code') ||
              suggestion.toLowerCase().includes('voici le code');

            return !containsCodeBlock && !mentionsSolution;
          });
        };

        const isSuccess = feedback.overallScore >= 70; // Consider 70+ as success

        // Parse the AI feedback into our UI format with new pedagogical structure
        setFeedback({
          success: isSuccess,
          message: feedback.logicAnalysis,
          suggestions: [
            // Legacy fallback for old feedback format
            ...filterCodeSuggestions(feedback.bestPractices || []),
            ...filterCodeSuggestions(feedback.improvements || []),
            ...(feedback.compilationStatus.errors.length > 0
              ? [`Erreurs de compilation: ${feedback.compilationStatus.errors.join(', ')}`]
              : []),
            ...(feedback.compilationStatus.warnings.length > 0
              ? [`Avertissements: ${feedback.compilationStatus.warnings.join(', ')}`]
              : [])
          ],
          detailedFeedback: {
            score: feedback.overallScore,
            compilationStatus: feedback.compilationStatus,
            conceptMastery: feedback.conceptMastery,
            correctSolution: feedback.correctSolution,
            // New pedagogical fields
            whatWorked: feedback.whatWorked || [],
            keyIssues: feedback.keyIssues || [],
            learningInsights: feedback.learningInsights || [],
            nextSteps: feedback.nextSteps || []
          }
        });

        trackEvent('feedback_viewed', { exerciseId, score: feedback.overallScore, learningMode });

        // Le compteur du point vient d'être mis à jour côté serveur : on le relit, et on annonce
        // l'acquisition quand CETTE soumission l'a fait passer au seuil. Un échec de lecture ne
        // gêne pas la correction.
        if (progressKpId) {
          getKnowledgePointProgress(user.id, progressKpId)
            .then(after => {
              if (progressKeyRef.current !== progressKeyAtSubmit) return; // l'élève est déjà passé à un autre exercice
              setKpProgress(after);
              if (isSuccess && after.acquired && progressBefore && !progressBefore.acquired) setAcquiredNow(after);
            })
            .catch(() => { /* le repère garde sa dernière valeur */ });
        }

        // B6 : compteur de réussites consécutives (localStorage) → CTA « Trop facile ? ».
        // Fire-and-forget : ne bloque jamais l'affichage du feedback.
        if (isSuccess) {
          recordExerciseSuccess(user.id)
            .then(({ propose, streak }) => {
              if (propose) {
                setCtaStreak(streak);
                setShowDifficultyCta(true);
              }
            })
            .catch(() => { /* jamais d'erreur remontée pour le CTA */ });
        } else {
          recordExerciseFailure(user.id);
        }
      } else {
        throw new Error('Erreur lors de l\'évaluation');
      }
    } catch (err) {
      console.error('❌ Erreur lors de l\'évaluation:', err);
      setFeedback({
        success: false,
        message: 'Erreur lors de l\'évaluation de votre code. Veuillez réessayer.',
        suggestions: ['Vérifiez votre connexion internet et réessayez.']
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    if (!exercise) return;

    // Reset to the original starting code from the exercise
    const initialCode = exercise.startingCode || getDefaultStartingCode();

    setCode(initialCode);
    setFeedback(null);
  };

  // B6 : CTA cliqué → événement + plus jamais proposé + ouverture de la modale existante
  const handleDifficultyCtaClick = () => {
    if (user) markDifficultyCtaClicked(user.id);
    trackEvent('difficulty_cta_clicked', { exerciseId, streak: ctaStreak, learningMode });
    setShowDifficultyCta(false);
    setIsDifficultyModalOpen(true);
  };

  // B6 : CTA fermé à la croix → re-proposé seulement après une longue série (voir utils/difficultyCta)
  const handleDifficultyCtaDismiss = () => {
    if (user) markDifficultyCtaDismissed(user.id);
    setShowDifficultyCta(false);
  };

  const handleBackToHome = () => {
    if (mode === 'review') {
      console.log('🏠 Retour à la progression');
      navigate('/progression');
    } else {
      console.log('🏠 Retour à la page d\'accueil');
      navigate('/');
    }
  };

  const handleContinueOnTopic = async (origin: FollowUpOrigin = 'feedback') => {
    if (!exercise || !user || followUpPending) return;

    trackEvent('continue_topic_clicked', { exerciseId, learningMode });
    setFollowUpFailure(null);

    try {
      setFollowUpPending('continue');

      console.log('🔄 Génération exercice similaire avec contexte d\'apprentissage:', learningNodeId, 'KPs:', knowledgePointIds);

      // Même niveau, autres conditions (charte §2.2). Le serveur relit cet exercice et écrit la
      // consigne lui-même : la page n'envoie que son identifiant.
      const created = await generateExercise({
        userId: user.id,
        concepts: exercise.concepts,
        difficulty: exercise.difficulty,
        adaptToProgress: false,
        learningMode,
        programmingLanguage,
        ...(learningNodeId && { learningNodeId }), // Preserve learning path context if available
        ...(knowledgePointIds.length > 0 && { knowledgePointIds }), // Preserve knowledge points context
        followUp: { kind: 'continue', previousExerciseId: exercise.id }
      });

      // Navigate to the new exercise, preserving learning path and knowledge points context
      if (learningNodeId || knowledgePointIds.length > 0) {
        console.log('✅ Contexte d\'apprentissage préservé pour le nouvel exercice:', { learningNodeId, knowledgePointIds });
      }
      navigate(generateExerciseUrl(created.id));
    } catch (err) {
      console.error('❌ Erreur lors de la génération d\'exercice similaire:', err);
      setFollowUpFailure({ kind: 'continue', origin, message: errorMessage(err, GENERATION_FAILED_FALLBACK) });
    } finally {
      setFollowUpPending(null);
    }
  };

  // « Connaissance suivante » : un exercice de parcours ordinaire (régime strict, sans suite de bouton) sur le point
  // qui suit dans l'étape, ou sur le début de l'étape suivante quand elle est ouverte.
  const handleNextKnowledge = async (origin: FollowUpOrigin = 'feedback') => {
    const target = knowledgeTarget(kpProgress);
    if (!exercise || !user || !target || followUpPending) return;

    trackEvent('next_knowledge_clicked', { exerciseId, learningMode, to: target.kind });
    setFollowUpFailure(null);

    try {
      setFollowUpPending('knowledge');

      const node = findPathNode(target.nodeId);
      const targetKpIds = target.kpId ? [target.kpId] : undefined;
      const created = await generateExercise({
        userId: user.id,
        concepts: node?.concepts ?? exercise.concepts,
        difficulty: node?.difficulty ?? exercise.difficulty,
        adaptToProgress: true,
        learningMode,
        programmingLanguage,
        learningNodeId: target.nodeId,
        ...(targetKpIds && { knowledgePointIds: targetKpIds }),
        customPrompt: buildPathCustomPrompt(target.nodeId, targetKpIds)
      });

      // Au début d'une étape, c'est le serveur qui choisit le point visé et le renvoie.
      navigate(buildExerciseUrl(created.id, target.nodeId, created.knowledgePointIds ?? targetKpIds ?? []));
    } catch (err) {
      console.error('❌ Erreur lors de la génération de l\'exercice de la connaissance suivante:', err);
      setFollowUpFailure({ kind: 'knowledge', origin, message: errorMessage(err, GENERATION_FAILED_FALLBACK) });
    } finally {
      setFollowUpPending(null);
    }
  };

  const handleNextExercise = async (origin: FollowUpOrigin = 'feedback') => {
    if (!exercise || !user || followUpPending) return;

    trackEvent('next_exercise_clicked', { exerciseId, learningMode });
    setFollowUpFailure(null);

    try {
      setFollowUpPending('next');

      console.log('🔄 Génération exercice progressif avec contexte d\'apprentissage:', learningNodeId, 'KPs:', knowledgePointIds);

      // Même sujet, un cran plus loin, sans sortir de l'étape (charte §2.2). Le serveur relit cet
      // exercice et écrit la consigne lui-même : la page n'envoie que son identifiant.
      const created = await generateExercise({
        userId: user.id,
        concepts: exercise.concepts,
        difficulty: exercise.difficulty,
        adaptToProgress: true,
        learningMode,
        programmingLanguage,
        ...(learningNodeId && { learningNodeId }), // Preserve learning path context if available
        ...(knowledgePointIds.length > 0 && { knowledgePointIds }), // Preserve knowledge points context
        followUp: { kind: 'next', previousExerciseId: exercise.id }
      });

      // Navigate to the new exercise, preserving learning path and knowledge points context
      if (learningNodeId || knowledgePointIds.length > 0) {
        console.log('✅ Contexte d\'apprentissage préservé pour le nouvel exercice:', { learningNodeId, knowledgePointIds });
      }
      navigate(generateExerciseUrl(created.id));
    } catch (err) {
      console.error('❌ Erreur lors de la génération d\'exercice progressif:', err);
      setFollowUpFailure({ kind: 'next', origin, message: errorMessage(err, GENERATION_FAILED_FALLBACK) });
    } finally {
      setFollowUpPending(null);
    }
  };

  // Un des trois boutons d'après-exercice, depuis la barre de l'éditeur ou depuis la correction
  const runFollowUp = (action: FollowUpAction, origin: FollowUpOrigin) => {
    if (action === 'continue') return handleContinueOnTopic(origin);
    if (action === 'next') return handleNextExercise(origin);
    return handleNextKnowledge(origin);
  };

  // Mini-cours component with progressive simplification
  // Panneau « Mini-cours : Aide contextuelle » — composant hissé dans components/ExerciseMiniCours.tsx

  // Console d'erreurs : erreurs/warnings de la dernière évaluation, ligne parsée en best-effort
  const compilationStatus = feedback?.detailedFeedback?.compilationStatus;
  const compilationIssues = useMemo<CompilationIssue[]>(() => {
    if (!compilationStatus) return [];
    return [
      ...(compilationStatus.errors ?? []).map((message) => ({ severity: 'error' as const, line: parseIssueLine(message), message })),
      ...(compilationStatus.warnings ?? []).map((message) => ({ severity: 'warning' as const, line: parseIssueLine(message), message })),
    ];
  }, [compilationStatus]);

  const issueLines = useMemo(
    () => compilationIssues
      .filter((issue) => issue.line !== null)
      .map((issue) => ({ line: issue.line as number, severity: issue.severity })),
    [compilationIssues]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement de l'exercice...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-600 mb-4">❌ {error}</div>
        <button
          onClick={handleBackToHome}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'accueil
        </button>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="text-center p-8">
        <div className="text-gray-500 mb-4">Exercice non trouvé</div>
        <button
          onClick={handleBackToHome}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'accueil
        </button>
      </div>
    );
  }

  // G2 — exercice pré-créé du simulateur (Time.deltaTime) : rendu par le simulateur (canvas + faux
  // compilateur + correction déterministe), pas par l'éditeur/soumission IA ci-dessous. En mode
  // révision, le code et le score de la tentative sont transmis ; tout le reste vit dans
  // components/simulator/. (Placé après tous les hooks : pas de hook conditionnel.)
  if (exercise.type === ExerciseType.INTERACTIVE_SIM) {
    return (
      <SimulatorExercise
        exercise={exercise}
        learningNodeId={learningNodeId}
        mode={mode}
        reviewAttempt={mode === 'review' ? reviewAttempt : null}
      />
    );
  }

  const starterTodos = extractStarterTodos(exercise.startingCode);

  // Determine if correction button should be shown
  const shouldShowCorrectionButton = (score: number): boolean => {
    if (isQuestExercise) {
      // For quest exercises: ignore user setting, only show for scores ≥ 90%
      return score >= QUEST_CORRECTION_THRESHOLD;
    } else {
      // For regular exercises:
      // - If user enabled the setting: show button normally (no score restriction)
      // - If user disabled the setting: only show for scores ≥ 90%
      if (userSettings.showCorrectionButton) {
        return true; // Show button regardless of score when setting is enabled
      } else {
        return score >= GENERAL_CORRECTION_THRESHOLD; // Only show for high scores when disabled
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {/* Header : padding réduit — le haut de page était trop vide (aveu M5) */}
        <div className="glass dark:glass-dark rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackToHome}
                className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {mode === 'review' ? 'Retour à la progression' : 'Retour aux exercices'}
              </button>
            </div>

            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
              {mode === 'review' && reviewAttempt && (
                <div className="flex items-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${reviewAttempt.isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                    {reviewAttempt.isCorrect ? 'Réussi' : 'Échoué'} - {reviewAttempt.timeSpent < 60 ? `${reviewAttempt.timeSpent}s` : `${Math.round(reviewAttempt.timeSpent / 60)}m`}
                  </span>
                </div>
              )}
              {mode !== 'review' && (
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {exercise.estimatedTimeMinutes || 5} min
                </div>
              )}
              <div className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                {exercise.difficulty}
              </div>
            </div>
          </div>

          {kpProgress && <KnowledgeProgressLine progress={kpProgress} className="mb-3" />}

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {mode === 'review' && '📋 Révision : '}
                <ReactMarkdown
                  components={{
                    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                    code: ({ children }) => <code className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>,
                    p: ({ children }) => <span>{children}</span>
                  }}
                >
                  {exercise.title}
                </ReactMarkdown>
              </h1>
              <div className="text-gray-600 dark:text-gray-300">
                <ReactMarkdown
                  components={{
                    strong: ({ children }) => <strong className="font-bold text-inherit">{children}</strong>,
                    code: ({ children }) => <code className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>,
                    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>
                  }}
                >
                  {exercise.description}
                </ReactMarkdown>
              </div>
            </div>

            {/* Admin Quest Button - Only show for admins and not in review mode */}
            {user?.role === 'admin' && mode !== 'review' && (
              <div className="ml-4 flex-shrink-0">
                <AdminQuestButton
                  exerciseId={exercise.id}
                  exerciseTitle={exercise.title}
                />
              </div>
            )}
          </div>
        </div>

        {/* Mini-cours : only show in learning mode, not in review */}
        {mode !== 'review' && <ExerciseMiniCours exercise={exercise} />}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Instructions */}
          <div className="glass dark:glass-dark rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Instructions</h2>
            <div className="prose prose-sm max-w-none">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <InstructionRenderer text={exercise.prompt} />
              </div>

              {starterTodos.length > 0 && (
                <div className="mt-4 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">
                    À faire dans le code
                  </h3>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 dark:text-slate-300">
                    {starterTodos.map((todo, index) => (
                      <li key={index}>{todo}</li>
                    ))}
                  </ul>
                </div>
              )}

              {exercise.context && (
                <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">
                    {learningMode === LearningMode.GODOT
                      ? 'Contexte Godot :'
                      : learningMode === LearningMode.PYTHON_JS
                        ? 'Contexte :'
                        : 'Contexte Unity :'}
                  </h3>
                  <div className="text-sm text-amber-700 dark:text-amber-400">
                    <ReactMarkdown
                      components={{
                        strong: ({ children }) => <strong className="font-bold text-amber-900 dark:text-amber-200">{children}</strong>,
                        code: ({ children }) => (
                          <code className="bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 px-1 py-0.5 rounded text-xs font-mono">
                            {children}
                          </code>
                        ),
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                      }}
                    >
                      {exercise.context}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Code Editor */}
          <div className="glass dark:glass-dark rounded-xl p-6">
            {/* `flex-wrap` + `gap` — quand le panneau du coach est ouvert à 1280 px,
                la colonne de l'éditeur ne fait plus que ~300 px et la barre « Réinitialiser · Coach ·
                Soumettre » (~316 px) débordait de 18 px sous le panneau (bouton Soumettre tronqué).
                Elle passe maintenant à la ligne. */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {mode === 'review' ? 'Votre code soumis' : 'Votre code'}
              </h2>
              <div className="flex flex-wrap gap-2">
                {mode === 'review' ? (
                  <>
                    <button
                      onClick={() => { trackEvent('chat_opened', { exerciseId }); setIsChatbotOpen(true); }}
                      className="px-3 py-1 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-200 border border-purple-300 dark:border-purple-700 rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center space-x-1"
                      title="Ouvrir le Coach de code"
                    >
                      <span>🤖</span>
                      <span>Coach</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleReset}
                      className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Réinitialiser
                    </button>
                    <button
                      onClick={() => { trackEvent('chat_opened', { exerciseId }); setIsChatbotOpen(true); }}
                      className="px-3 py-1 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-200 border border-purple-300 dark:border-purple-700 rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center space-x-1"
                      title="Ouvrir le Coach de code"
                    >
                      <span>🤖</span>
                      <span>Coach</span>
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Évaluation...
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-1" />
                          Soumettre
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
            {mode === 'review' && (
              <FollowUpButtons
                className="mb-4"
                progress={kpProgress}
                pending={followUpPending}
                disabled={isSubmitting}
                onAction={(action) => runFollowUp(action, 'editor')}
              />
            )}
            {followUpFailure?.origin === 'editor' && (
              <div ref={followUpFailureRef}>
                <GenerationFailedNotice
                  className="mb-4"
                  message={followUpFailure.message}
                  onRetry={() => runFollowUp(followUpFailure.kind, followUpFailure.origin)}
                  onDismiss={() => setFollowUpFailure(null)}
                />
              </div>
            )}

            <CodeEditor
              value={code}
              onChange={mode === 'review' ? () => { } : setCode} // Read-only in review mode
              language={learningMode === LearningMode.PYTHON_JS ? (programmingLanguage === ProgrammingLanguage.PYTHON ? 'Python' : 'JavaScript') : learningMode === LearningMode.GODOT ? 'GDScript' : 'C#'}
              isFullScreen={isCodeFullScreen}
              onToggleFullScreen={() => {
                if (!isCodeFullScreen) trackEvent('fullscreen_enabled', { exerciseId });
                setIsCodeFullScreen(!isCodeFullScreen);
              }}
              issueLines={issueLines}
            />

            {/* Console d'erreurs : visible dès qu'une évaluation a rendu un compilationStatus.
                Fond sombre volontaire même en light mode (console IDE). Panneau extensible :
                la ligne de statut scoring (A4) viendra s'ajouter sous la liste. */}
            {compilationStatus && !compilationStatus.notAnalyzed && (
              <div className="mt-4 bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
                  <div className="flex items-center space-x-2 text-gray-300 text-xs font-medium uppercase tracking-wide">
                    <Terminal className="w-3.5 h-3.5" />
                    <span>Console</span>
                  </div>
                  <span
                    className="text-[11px] px-2 py-0.5 rounded-full bg-blue-900/60 text-blue-300 border border-blue-800"
                    title="Erreurs détectées par l'évaluation IA, pas par un vrai compilateur"
                  >
                    Analyse IA
                  </span>
                </div>
                <div className="px-3 py-2 font-mono text-sm space-y-1">
                  {compilationIssues.length === 0 ? (
                    compilationStatus.wouldCompile ? (
                      <div className="text-green-500/90">✓ Aucune erreur détectée</div>
                    ) : (
                      <div className="text-red-400">✗ Code non compilable (aucun détail fourni par l'analyse)</div>
                    )
                  ) : (
                    compilationIssues.map((issue, index) => (
                      <div
                        key={index}
                        className={`whitespace-pre-wrap break-words ${issue.severity === 'error' ? 'text-red-400' : 'text-amber-300'}`}
                      >
                        {issue.severity === 'error' ? '✗' : '⚠'} {issue.message}
                      </div>
                    ))
                  )}
                  {/* A4 : transparence du plafond de score (champ absent sur les anciens feedbacks → rien) */}
                  {compilationStatus.scoreCapped && (
                    <div className="pt-1.5 mt-1.5 border-t border-gray-700 text-amber-300">
                      ⚠ Score plafonné à 60 : le code ne compile pas
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Pedagogical Feedback */}
        {feedback && (
          <div id="feedback-section" className="glass dark:glass-dark rounded-xl overflow-hidden">
            {/* Header with score */}
            <div className={`px-6 py-4 ${feedback.success ? 'bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {feedback.success ? (
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-500" />
                  )}
                  <div>
                    <h3 className={`text-lg font-semibold ${feedback.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                      {feedback.success ? 'Excellent travail !' : 'Continuez vos efforts !'}
                    </h3>
                    {feedback.detailedFeedback?.score !== undefined && (
                      <p className={`text-sm ${feedback.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        Score: {feedback.detailedFeedback.score}/100
                      </p>
                    )}
                    {/* A4 : transparence du plafond (le plafond implique un échec → palette rouge du bandeau) */}
                    {feedback.detailedFeedback?.compilationStatus?.scoreCapped && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                        Score plafonné à 60 : le code ne compile pas
                      </p>
                    )}
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${feedback.success ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                  }`}>
                  {feedback.success ? '✓ Réussi' : '⚠ À améliorer'}
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Annonce quand cette soumission fait atteindre le seuil du point de connaissance */}
              {acquiredNow && <KnowledgeAcquiredNotice progress={acquiredNow} />}

              {/* Main pedagogical analysis */}
              <div>
                <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  📊 Analyse de votre approche
                </h4>
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                  <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    <ReactMarkdown
                      components={{
                        strong: ({ children }) => <strong className="font-bold text-blue-800">{children}</strong>,
                        code: ({ children }) => <code className="bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>,
                        p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>
                      }}
                    >
                      {feedback.message}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>

              {/* What worked well */}
              {feedback.detailedFeedback?.whatWorked && feedback.detailedFeedback.whatWorked.length > 0 && (
                <div>
                  <h4 className="flex items-center text-lg font-semibold text-green-800 dark:text-green-300 mb-3">
                    ✅ Ce que vous avez bien fait
                  </h4>
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <ul className="space-y-2">
                      {feedback.detailedFeedback.whatWorked.map((item, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                          <div className="text-green-800 dark:text-green-300 flex-1">
                            <ReactMarkdown
                              components={{
                                strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                                code: ({ children }) => <code className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-1 py-0.5 rounded text-sm font-mono">{children}</code>,
                                p: ({ children }) => <span>{children}</span>
                              }}
                            >
                              {item}
                            </ReactMarkdown>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Key issues to address */}
              {feedback.detailedFeedback?.keyIssues && feedback.detailedFeedback.keyIssues.length > 0 && (
                <div>
                  <h4 className="flex items-center text-lg font-semibold text-red-800 dark:text-red-300 mb-3">
                    ⚠️ Points principaux à corriger
                  </h4>
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <ul className="space-y-2">
                      {feedback.detailedFeedback.keyIssues.map((issue, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                          <div className="text-red-800 dark:text-red-300 flex-1">
                            <ReactMarkdown
                              components={{
                                strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                                code: ({ children }) => <code className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-1 py-0.5 rounded text-sm font-mono">{children}</code>,
                                p: ({ children }) => <span>{children}</span>
                              }}
                            >
                              {issue}
                            </ReactMarkdown>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Compilation status */}
              {feedback.detailedFeedback?.compilationStatus && !feedback.detailedFeedback.compilationStatus.notAnalyzed && (
                <div>
                  <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    🔧 État de compilation
                  </h4>
                  <div className="flex items-center space-x-2 mb-3">
                    {feedback.detailedFeedback.compilationStatus.wouldCompile ? (
                      <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-sm rounded-full">✓ Code compilable</span>
                    ) : (
                      <span className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-sm rounded-full">✗ Erreurs de compilation</span>
                    )}
                  </div>

                  {feedback.detailedFeedback.compilationStatus.errors.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 rounded-lg mb-2">
                      <h5 className="font-medium text-red-800 dark:text-red-300 mb-2">Erreurs à corriger :</h5>
                      <ul className="list-disc list-inside text-red-700 dark:text-red-400 text-sm space-y-1">
                        {feedback.detailedFeedback.compilationStatus.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {feedback.detailedFeedback.compilationStatus.warnings.length > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-3 rounded-lg">
                      <h5 className="font-medium text-yellow-800 dark:text-yellow-300 mb-2">Avertissements :</h5>
                      <ul className="list-disc list-inside text-yellow-700 dark:text-yellow-400 text-sm space-y-1">
                        {feedback.detailedFeedback.compilationStatus.warnings.map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Learning insights */}
              {feedback.detailedFeedback?.learningInsights && feedback.detailedFeedback.learningInsights.length > 0 && (
                <div>
                  <h4 className="flex items-center text-lg font-semibold text-blue-800 dark:text-blue-300 mb-3">
                    💡 Concepts à approfondir
                  </h4>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <ul className="space-y-2">
                      {feedback.detailedFeedback.learningInsights.map((insight, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                          <div className="text-blue-800 dark:text-blue-300 flex-1">
                            <ReactMarkdown
                              components={{
                                strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                                code: ({ children }) => <code className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1 py-0.5 rounded text-sm font-mono">{children}</code>,
                                p: ({ children }) => <span>{children}</span>
                              }}
                            >
                              {insight}
                            </ReactMarkdown>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Next steps */}
              {feedback.detailedFeedback?.nextSteps && feedback.detailedFeedback.nextSteps.length > 0 && (
                <div>
                  <h4 className="flex items-center text-lg font-semibold text-purple-800 dark:text-purple-300 mb-3">
                    🎯 Prochaines étapes
                  </h4>
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                    <ol className="space-y-2">
                      {feedback.detailedFeedback.nextSteps.map((step, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                            {index + 1}
                          </span>
                          <div className="text-purple-800 dark:text-purple-300 flex-1">
                            <ReactMarkdown
                              components={{
                                strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                                code: ({ children }) => <code className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-1 py-0.5 rounded text-sm font-mono">{children}</code>,
                                p: ({ children }) => <span>{children}</span>
                              }}
                            >
                              {step}
                            </ReactMarkdown>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              )}

              {/* Correction section */}
              {feedback.detailedFeedback?.correctSolution &&
                shouldShowCorrectionButton(feedback.detailedFeedback.score || 0) && (
                  <CorrectionSection
                    correctSolution={feedback.detailedFeedback.correctSolution}
                    onReveal={setRevealedSolution}
                  />
                )}

              {/* Success actions */}
              {feedback.success && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                  {/* B6 : CTA discret de découvrabilité de la difficulté, après une série de réussites */}
                  {showDifficultyCta && (
                    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-300 min-w-0">
                        <Sliders className="w-4 h-4 flex-shrink-0" />
                        <span>Trop facile ? Vous venez de réussir {ctaStreak} exercices d'affilée.</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={handleDifficultyCtaClick}
                          className="px-3 py-1 text-sm font-medium text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                        >
                          Ajuster la difficulté
                        </button>
                        <button
                          onClick={handleDifficultyCtaDismiss}
                          className="p-1 text-blue-400 hover:text-blue-600 dark:text-blue-500 dark:hover:text-blue-300 rounded transition-colors"
                          title="Ne plus proposer pour le moment"
                          aria-label="Fermer la suggestion de difficulté"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Le même repère qu'en haut de page : c'est ici que l'élève regarde quand il a fini. */}
                  {kpProgress && <KnowledgeProgressLine progress={kpProgress} />}
                  <FollowUpButtons
                    progress={kpProgress}
                    pending={followUpPending}
                    disabled={isSubmitting}
                    onAction={(action) => runFollowUp(action, 'feedback')}
                  />
                  {followUpFailure?.origin === 'feedback' && (
                    <div ref={followUpFailureRef}>
                      <GenerationFailedNotice
                        message={followUpFailure.message}
                        onRetry={() => runFollowUp(followUpFailure.kind, followUpFailure.origin)}
                        onDismiss={() => setFollowUpFailure(null)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pedagogical Chatbot */}
      {exercise && (
        <PedagogicalChatbot
          exercise={exercise}
          currentCode={code}
          feedback={feedback ?? undefined}
          miniCoursGenerated={false} // TODO: Track mini-cours generation state
          correctionViewed={!!revealedSolution && revealedSolution === feedback?.detailedFeedback?.correctSolution}
          isOpen={isChatbotOpen}
          onClose={() => setIsChatbotOpen(false)}
          mode={mode}
        />
      )}

      {/* B6 : modale de difficulté existante (aucune modif), ouverte depuis le CTA.
          Réglages sauvegardés → le CTA n'a plus de raison d'être. */}
      <DifficultySettingsModal
        isOpen={isDifficultyModalOpen}
        onClose={() => setIsDifficultyModalOpen(false)}
        onSettingsUpdated={() => { if (user) markDifficultyAdjusted(user.id); }}
      />
    </div>
  );
};

export default ExercisePage; 